// /opt/isp_erp/frontend/src/components/tickets/FotoUploader.jsx

import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Grid,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  Compress as CompressIcon
} from '@mui/icons-material';
import { compressImage, compressMultipleImages } from '../../utils/imageCompressor';

const FotoUploader = ({ onFotosChange, maxFotos = 5 }) => {
  const [fotos, setFotos] = useState([]);
  const [error, setError] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [comprimiendo, setComprimiendo] = useState(false);

  const convertirABase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const validarArchivo = (file) => {
    const tiposValidos = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    const maxSize = 10 * 1024 * 1024; // 10MB antes de comprimir

    if (!tiposValidos.includes(file.type)) {
      throw new Error('Formato no soportado. Use JPEG, PNG o WEBP');
    }
    if (file.size > maxSize) {
      throw new Error('La imagen no debe superar los 10MB');
    }
    return true;
  };

  const handleAgregarFotos = async (event) => {
    const archivos = Array.from(event.target.files);
    setError('');
    setComprimiendo(true);

    if (fotos.length + archivos.length > maxFotos) {
      setError(`Máximo ${maxFotos} fotos permitidas`);
      setComprimiendo(false);
      return;
    }

    const nuevasFotos = [];

    for (const archivo of archivos) {
      try {
        validarArchivo(archivo);

        // Crear preview (sin comprimir para velocidad)
        const preview = URL.createObjectURL(archivo);

        // Comprimir imagen para reducir tamaño
        let base64;
        try {
          // Comprimir a 1024px de ancho, calidad 0.6 (más agresivo para celular)
          base64 = await compressImage(archivo, 1024, 0.6);
          console.log(`✅ Imagen comprimida: ${(archivo.size / 1024).toFixed(1)}KB -> ${(base64.length * 0.75 / 1024).toFixed(1)}KB`);
        } catch (compressError) {
          console.error('Error comprimiendo, usando original:', compressError);
          base64 = await convertirABase64(archivo);
        }

        nuevasFotos.push({
          archivo,
          preview,
          base64,
          descripcion: `Foto ${fotos.length + nuevasFotos.length + 1}`,
          size: archivo.size
        });
      } catch (err) {
        setError(err.message);
      }
    }

    const fotosActualizadas = [...fotos, ...nuevasFotos];
    setFotos(fotosActualizadas);

    // Enviar solo los base64 comprimidos al padre
    onFotosChange(fotosActualizadas.map(f => f.base64));
    setComprimiendo(false);

    // Limpiar el input
    event.target.value = '';
  };

  const handleEliminarFoto = (index) => {
    const fotosActualizadas = fotos.filter((_, i) => i !== index);

    if (fotos[index].preview) {
      URL.revokeObjectURL(fotos[index].preview);
    }

    setFotos(fotosActualizadas);
    onFotosChange(fotosActualizadas.map(f => f.base64));
  };

  const handleDescripcionChange = (index, descripcion) => {
    const fotosActualizadas = [...fotos];
    fotosActualizadas[index].descripcion = descripcion;
    setFotos(fotosActualizadas);
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddPhotoIcon color="primary" />
        Fotos de Instalación ({fotos.length}/{maxFotos})
      </Typography>

      <Button
        variant="outlined"
        component="label"
        startIcon={comprimiendo ? <CircularProgress size={20} /> : <AddPhotoIcon />}
        disabled={fotos.length >= maxFotos || comprimiendo}
        fullWidth
        sx={{ mb: 2 }}
      >
        {comprimiendo ? 'Comprimiendo imágenes...' : 'Seleccionar Fotos'}
        <input
          type="file"
          hidden
          multiple
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleAgregarFotos}
          capture="environment" // Para celular: usa cámara trasera
        />
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {fotos.length > 0 && (
        <ImageList cols={2} gap={8} sx={{ mb: 2 }}>
          {fotos.map((foto, idx) => (
            <ImageListItem key={idx}>
              <img
                src={foto.preview}
                alt={`Foto ${idx + 1}`}
                loading="lazy"
                style={{
                  cursor: 'pointer',
                  height: 150,
                  width: '100%',
                  objectFit: 'cover',
                  borderRadius: 4
                }}
              />
              <ImageListItemBar
                title={
                  <TextField
                    size="small"
                    placeholder="Descripción (opcional)"
                    value={foto.descripcion}
                    onChange={(e) => handleDescripcionChange(idx, e.target.value)}
                    variant="standard"
                    fullWidth
                    sx={{
                      '& input': { color: 'white', fontSize: '0.75rem' }
                    }}
                  />
                }
                actionIcon={
                  <IconButton
                    sx={{ color: 'white' }}
                    onClick={() => handleEliminarFoto(idx)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                position="bottom"
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      <Typography variant="caption" color="text.secondary">
        Formatos: JPEG, PNG, WEBP | Las fotos se comprimen automáticamente | Hasta {maxFotos} fotos
      </Typography>
    </Paper>
  );
};

export default FotoUploader;
